import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { mapaPunto, whatsappEnlace } from '../lib/mapas';

const enlacePublico = (token) => `${window.location.origin}/cita-segura/${token}`;

function ubicacionActual() {
  return new Promise(resolve => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      p => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
      () => resolve(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 60000 },
    );
  });
}

// Cita segura (todos los planes): una persona de confianza sabe dónde y con quién estás, y tenés un botón de ayuda
export default function CitaSeguraModal({ match, onClose, onError }) {
  const [cita, setCita] = useState(undefined);
  const [form, setForm] = useState({ lugar: '', fecha_hora: '', contacto_nombre: '', contacto_telefono: '' });
  const [enviando, setEnviando] = useState(false);
  const [ayuda, setAyuda] = useState(null); // { lat, lng } o {} si no hubo ubicación
  const [terminada, setTerminada] = useState(false);
  const [lugares, setLugares] = useState([]); // locales adheridos a Cupido Descuentos, sugeridos como lugar de la cita

  useEffect(() => {
    api.citaSeguraActiva(match.matchId).then(d => setCita(d.cita)).catch(() => setCita(null));
    api.descuentos().then(d => setLugares((d.descuentos || []).slice(0, 8))).catch(() => {});
  }, [match.matchId]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const crear = async (e) => {
    e.preventDefault();
    setEnviando(true);
    try { const d = await api.crearCitaSegura({ ...form, match_id: match.matchId }); setCita(d.cita); }
    catch (err) { onError(err.message || 'No se pudo activar Cita segura.'); }
    finally { setEnviando(false); }
  };

  const pedirAyuda = async () => {
    setEnviando(true);
    const pos = await ubicacionActual();
    try { await api.pedirAyudaCita(cita.id, pos || {}); } catch (err) { /* igual mostramos cómo avisar */ }
    setAyuda(pos || {});
    setEnviando(false);
  };

  const terminar = async () => {
    try { await api.terminarCitaSegura(cita.id); setTerminada(true); }
    catch (err) { onError(err.message || 'No se pudo terminar la cita.'); }
  };

  const textoInicial = (c) =>
    `Hola ${c.contacto_nombre}, te aviso que tengo una cita${c.con_nombre ? ` con ${c.con_nombre}` : ''} en ${c.lugar} (${c.fecha_hora}). ` +
    `Si necesito ayuda te va a llegar mi ubicación. Podés seguirlo acá: ${enlacePublico(c.token)}`;
  const textoAyuda = (c, pos) =>
    `🆘 ${c.contacto_nombre}, necesito ayuda. Estoy en mi cita en ${c.lugar}.` +
    (pos?.lat != null ? ` Mi ubicación: ${mapaPunto(pos.lat, pos.lng)}` : '') + ` Más datos: ${enlacePublico(c.token)}`;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal cita-segura-modal" onClick={e => e.stopPropagation()}>
        <h2 style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>🛡️ Cita segura</h2>

        {cita === undefined ? (
          <p style={{ color: 'var(--texto-muted)' }}>Cargando…</p>
        ) : terminada ? (
          <>
            <p style={{ marginBottom: 16 }}>💚 ¡Qué bueno que estés bien! Avisale a {cita.contacto_nombre} que ya terminó.</p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <a className="btn btn-primary" target="_blank" rel="noopener noreferrer"
                href={whatsappEnlace(cita.whatsapp, `${cita.contacto_nombre}, ya terminó mi cita y estoy bien. ¡Gracias! 💚`)}>Avisar por WhatsApp</a>
              <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
            </div>
          </>
        ) : ayuda ? (
          <>
            <p style={{ marginBottom: 16 }}>
              Avisale ya a {cita.contacto_nombre}{ayuda.lat != null ? ' con tu ubicación' : ''}. Si estás en peligro, llamá al 911.
            </p>
            <div className="cita-segura-acciones">
              <a className="btn btn-primary btn-lg" target="_blank" rel="noopener noreferrer"
                href={whatsappEnlace(cita.whatsapp, textoAyuda(cita, ayuda))}>📲 Avisar a {cita.contacto_nombre}</a>
              <a className="btn btn-lg cita-segura-911" href="tel:911">📞 Llamar al 911</a>
            </div>
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 14 }} onClick={() => setAyuda(null)}>Volver</button>
          </>
        ) : cita ? (
          <>
            <p style={{ color: 'var(--texto-suave)', marginBottom: 12 }}>
              Cita en <strong>{cita.lugar}</strong> ({cita.fecha_hora}). Persona de confianza: <strong>{cita.contacto_nombre}</strong>.
            </p>
            <a className="btn btn-outline btn-sm" target="_blank" rel="noopener noreferrer" style={{ marginBottom: 16 }}
              href={whatsappEnlace(cita.whatsapp, textoInicial(cita))}>📤 Mandarle los datos por WhatsApp</a>
            <div className="cita-segura-acciones">
              <button className="btn btn-lg cita-segura-ayuda" onClick={pedirAyuda} disabled={enviando}>
                {enviando ? 'Buscando tu ubicación…' : '🆘 Necesito ayuda'}
              </button>
              <button className="btn btn-lg btn-primary" onClick={terminar}>💚 Terminé, estoy bien</button>
            </div>
          </>
        ) : (
          <form onSubmit={crear}>
            <p style={{ color: 'var(--texto-suave)', marginBottom: 16, fontSize: 14 }}>
              Tu persona de confianza va a recibir un enlace con dónde, cuándo y con quién es la cita. Si tocás "Necesito ayuda", le mandás tu ubicación.
            </p>
            <div className="form-group"><label className="form-label">¿Dónde?</label>
              <input className="form-input" required maxLength={150} value={form.lugar} onChange={set('lugar')} placeholder="Ej: Café del Parque, Av. Libertador 1234" />
              {lugares.length > 0 && (
                <div className="cita-lugares">
                  <span className="cita-lugares-titulo">🏷️ Lugares adheridos con descuento:</span>
                  {lugares.map(l => (
                    <button type="button" key={l.id} className="afinidad-opcion"
                      onClick={() => setForm(f => ({ ...f, lugar: [l.nombre_local, l.direccion].filter(Boolean).join(', ').slice(0, 150) }))}>
                      {l.nombre_local} · {l.descuento}
                    </button>
                  ))}
                </div>
              )}</div>
            <div className="form-group"><label className="form-label">¿Cuándo?</label>
              <input className="form-input" required maxLength={60} value={form.fecha_hora} onChange={set('fecha_hora')} placeholder="Ej: hoy a las 21 h" /></div>
            <div className="form-group"><label className="form-label">Nombre de tu persona de confianza</label>
              <input className="form-input" required maxLength={60} value={form.contacto_nombre} onChange={set('contacto_nombre')} placeholder="Ej: Caro" /></div>
            <div className="form-group"><label className="form-label">Su WhatsApp</label>
              <input className="form-input" required inputMode="tel" maxLength={20} value={form.contacto_telefono} onChange={set('contacto_telefono')} placeholder="Ej: 264 4123456" /></div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancelar</button>
              <button className="btn btn-primary" disabled={enviando}>{enviando ? 'Activando…' : 'Activar Cita segura'}</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
